import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Cell,
} from "recharts";
import {
  getRequestTypeAnalytics,
  listRequestApplications,
  listRequestTypes,
  submitRequestApplication,
} from "../../api/requests";
import { Modal } from "../../components/modal";
import { readSessionCache, writeSessionCache } from "../../utils/sessionCache";
import ExpandableSearch from "../../components/ExpandableSearch";
import ViewActionButton from "../../components/table/ViewActionButton";
import FetchLoadingState from "../../components/feedback/FetchLoadingState";

const STATUS_CLASS = {
  Pending: "text-amber-700 bg-amber-50",
  Approved: "text-green-700 bg-green-50",
  Declined: "text-red-700 bg-red-50",
};

const COLORS = ["#1234CD", "#22C55E", "#EF4444"];

export default function RequestTablePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialRequestTypeId = searchParams.get("requestTypeId") || "";
  const cacheKey = useMemo(
    () => `cehr_request_table_page_${initialRequestTypeId || "all"}`,
    [initialRequestTypeId],
  );
  const cachedData = useMemo(
    () =>
      readSessionCache(cacheKey, {
        requestTypes: [],
        applications: [],
        analytics: null,
      }),
    [cacheKey],
  );

  const [requestTypeId, setRequestTypeId] = useState(initialRequestTypeId);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [loading, setLoading] = useState(!(cachedData?.requestTypes?.length || cachedData?.applications?.length));
  const [requestTypes, setRequestTypes] = useState(cachedData?.requestTypes || []);
  const [applications, setApplications] = useState(cachedData?.applications || []);
  const [analytics, setAnalytics] = useState(cachedData?.analytics || null);
  const [searchTerm, setSearchTerm] = useState("");

  const [applyModalOpen, setApplyModalOpen] = useState(false);
  const [submittingApplication, setSubmittingApplication] = useState(false);
  const [applicationForm, setApplicationForm] = useState({
    requestTypeId: initialRequestTypeId,
    applicantName: "",
    applicantEmail: "",
    applicantPhone: "",
    responses: {},
  });

  const selectedRequestType = useMemo(
    () => requestTypes.find((item) => item.id === applicationForm.requestTypeId),
    [requestTypes, applicationForm.requestTypeId]
  );

  const filteredApplications = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return applications;
    return applications.filter((application) =>
      [application.applicantName, application.applicantEmail, application.applicantPhone, application.requestType?.name, application.status]
        .some((value) => String(value || "").toLowerCase().includes(query)),
    );
  }, [applications, searchTerm]);

  const fetchPageData = useCallback(async (nextRequestTypeId = requestTypeId, options = {}) => {
    const hasStaleData = requestTypes.length > 0 || applications.length > 0;

    if (!options?.silent && !hasStaleData) {
      setLoading(true);
    }

    try {
      const [typesRes, applicationsRes] = await Promise.all([
        listRequestTypes({ includeInactive: true }),
        listRequestApplications({ requestTypeId: nextRequestTypeId || undefined, limit: 50 }),
      ]);

      const nextRequestTypes = typesRes?.requestTypes || [];
      const nextApplications = applicationsRes?.applications || [];
      let nextAnalytics = null;

      setRequestTypes(nextRequestTypes);
      setApplications(nextApplications);

      if (showAnalytics && nextRequestTypeId) {
        const analyticsRes = await getRequestTypeAnalytics(nextRequestTypeId);
        nextAnalytics = analyticsRes?.analytics || null;
      }

      setAnalytics(nextAnalytics);
      writeSessionCache(`cehr_request_table_page_${nextRequestTypeId || "all"}`, {
        requestTypes: nextRequestTypes,
        applications: nextApplications,
        analytics: nextAnalytics,
      });
    } catch (error) {
      const message = error?.response?.data?.message || "Unable to load request applications.";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [applications.length, requestTypeId, requestTypes.length, showAnalytics]);

  useEffect(() => {
    fetchPageData(requestTypeId, { silent: true });
  }, [fetchPageData, requestTypeId]);

  useEffect(() => {
    const handleFocusRefresh = () => {
      if (document.visibilityState !== "hidden") {
        fetchPageData(requestTypeId, { silent: true });
      }
    };

    window.addEventListener("focus", handleFocusRefresh);
    document.addEventListener("visibilitychange", handleFocusRefresh);

    return () => {
      window.removeEventListener("focus", handleFocusRefresh);
      document.removeEventListener("visibilitychange", handleFocusRefresh);
    };
  }, [fetchPageData, requestTypeId]);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (requestTypeId) {
      params.set("requestTypeId", requestTypeId);
    } else {
      params.delete("requestTypeId");
    }
    setSearchParams(params);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestTypeId]);

  const handleRequestTypeFilter = async (event) => {
    const nextRequestTypeId = event.target.value;
    setRequestTypeId(nextRequestTypeId);

    const nextCachedData = readSessionCache(`cehr_request_table_page_${nextRequestTypeId || "all"}`, null);
    if (nextCachedData) {
      setRequestTypes(nextCachedData.requestTypes || []);
      setApplications(nextCachedData.applications || []);
      setAnalytics(nextCachedData.analytics || null);
    }
  };

  const handleToggleAnalytics = async () => {
    const nextState = !showAnalytics;
    setShowAnalytics(nextState);

    if (nextState && requestTypeId) {
      try {
        const analyticsRes = await getRequestTypeAnalytics(requestTypeId);
        setAnalytics(analyticsRes?.analytics || null);
      } catch (error) {
        const message = error?.response?.data?.message || "Unable to load analytics.";
        toast.error(message);
      }
    }
  };

  const openApplyModal = () => {
    setApplicationForm({
      requestTypeId,
      applicantName: "",
      applicantEmail: "",
      applicantPhone: "",
      responses: {},
    });
    setApplyModalOpen(true);
  };

  const handleSubmitApplication = async () => {
    if (submittingApplication) {
      return;
    }

    if (!selectedRequestType) {
      toast.error("Select a request type before submitting.");
      return;
    }

    if (!applicationForm.applicantName.trim() || !applicationForm.applicantEmail.trim()) {
      toast.error("Applicant name and email are required.");
      return;
    }

    const missingRequired = (selectedRequestType.fields || []).find(
      (field) => field.isRequired && !applicationForm.responses[field.id]
    );

    if (missingRequired) {
      toast.error(`${missingRequired.label} is required.`);
      return;
    }

    const responses = (selectedRequestType.fields || []).map((field) => ({
      fieldId: field.id,
      label: field.label,
      value: applicationForm.responses[field.id] || "",
    }));

    try {
      setSubmittingApplication(true);
      await submitRequestApplication({
        requestTypeId: selectedRequestType.id,
        applicantName: applicationForm.applicantName.trim(),
        applicantEmail: applicationForm.applicantEmail.trim(),
        applicantPhone: applicationForm.applicantPhone.trim() || undefined,
        responses,
      });

      toast.success("Request submitted successfully.");
      setApplyModalOpen(false);
      await fetchPageData(selectedRequestType.id);
    } catch (error) {
      const message = error?.response?.data?.message || "Unable to submit request.";
      toast.error(message);
    } finally {
      setSubmittingApplication(false);
    }
  };

  const pieData = analytics
    ? [
        { name: "Pending", value: analytics.summary?.Pending || 0 },
        { name: "Approved", value: analytics.summary?.Approved || 0 },
        { name: "Declined", value: analytics.summary?.Declined || 0 },
      ]
    : [];

  return (
    <div className="p-6 font-Outfit min-h-screen">
      <div className="flex flex-wrap gap-3 items-center justify-between mb-5">
        <h2 className="text-2xl font-semibold text-[#071C50]">Request Applications</h2>

        <div className="flex flex-wrap items-center gap-2">
          <select
            className="border border-[#D0D5DD] rounded-md px-3 py-2 text-sm"
            value={requestTypeId}
            onChange={handleRequestTypeFilter}
          >
            <option value="">All request types</option>
            {requestTypes.map((requestType) => (
              <option
                key={requestType.id}
                value={requestType.id}
              >
                {requestType.name}
              </option>
            ))}
          </select>

          <button
            className="px-4 py-2 rounded-md bg-[#1234CD] text-white text-sm"
            onClick={openApplyModal}
          >
            Send Request
          </button>

          <button
            className="px-4 py-2 rounded-md border border-[#1234CD] text-[#1234CD] text-sm"
            onClick={handleToggleAnalytics}
          >
            {showAnalytics ? "Hide Analytics" : "View Analytics"}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-[#EEF0F6] shadow-sm p-5">
        <ExpandableSearch
          value={searchTerm}
          onChange={setSearchTerm}
          placeholder="Search applications"
          label="Search request applications"
          className="mb-4"
        />
        {loading ? (
          <FetchLoadingState message="Loading applications..." className="min-h-[180px]" />
        ) : (
          <div className="w-full max-w-full overflow-x-auto overscroll-x-contain">
            <table className="w-full min-w-[980px] text-left">
              <thead>
                <tr className="text-sm text-[#667085] border-b">
                  <th className="py-2">Applicant</th>
                  <th className="py-2">Request</th>
                  <th className="py-2">Email</th>
                  <th className="py-2">Phone</th>
                  <th className="py-2">Date</th>
                  <th className="py-2">Status</th>
                  <th className="py-2">Action</th>
                </tr>
              </thead>

              <tbody>
                {filteredApplications.map((application) => (
                  <tr
                    key={application.id}
                    className="border-b last:border-b-0 text-sm"
                  >
                    <td className="py-3">{application.applicantName}</td>
                    <td className="py-3">{application.requestType?.name || "-"}</td>
                    <td className="py-3">{application.applicantEmail}</td>
                    <td className="py-3">{application.applicantPhone || "-"}</td>
                    <td className="py-3">{new Date(application.submittedAt).toLocaleDateString()}</td>
                    <td className="py-3">
                      <span className={`px-2.5 py-1 rounded-full text-xs ${STATUS_CLASS[application.status] || ""}`}>
                        {application.status}
                      </span>
                    </td>
                    <td className="py-3">
                      <ViewActionButton
                        to={`/requests/request-details?applicationId=${application.id}`}
                        label={`View request from ${application.applicantName || "applicant"}`}
                      />
                    </td>
                  </tr>
                ))}

                {filteredApplications.length === 0 && (
                  <tr>
                    <td
                      colSpan={7}
                      className="py-8 text-center text-sm text-[#98A2B3]"
                    >
                      {searchTerm ? "No matching applications found." : "No applications found."}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAnalytics && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-5">
          <div className="bg-white rounded-xl border border-[#EEF0F6] shadow-sm p-5">
            <h3 className="text-base font-semibold text-[#071C50] mb-4">Status Distribution</h3>
            <ResponsiveContainer
              width="100%"
              height={260}
            >
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={4}
                >
                  {pieData.map((entry, index) => (
                    <Cell
                      key={`${entry.name}-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white rounded-xl border border-[#EEF0F6] shadow-sm p-5">
            <h3 className="text-base font-semibold text-[#071C50] mb-4">Application Timeline</h3>
            <ResponsiveContainer
              width="100%"
              height={260}
            >
              <LineChart data={analytics?.timeline || []}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="total"
                  stroke="#1234CD"
                  strokeWidth={3}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <Modal
        isOpen={applyModalOpen}
        onClose={() => {
          if (!submittingApplication) {
            setApplyModalOpen(false);
          }
        }}
      >
        <div className="font-Outfit">
          <h3 className="text-lg font-semibold mb-4">Send Request</h3>

          <div className="space-y-4">
            <select
              value={applicationForm.requestTypeId}
              onChange={(event) =>
                setApplicationForm((previous) => ({
                  ...previous,
                  requestTypeId: event.target.value,
                  responses: {},
                }))
              }
              className="w-full border border-[#D0D5DD] rounded-md px-3 py-2 outline-none"
            >
              <option value="">Select request type</option>
              {requestTypes
                .filter((requestType) => requestType.isActive)
                .map((requestType) => (
                  <option
                    key={requestType.id}
                    value={requestType.id}
                  >
                    {requestType.name}
                  </option>
                ))}
            </select>

            <input
              value={applicationForm.applicantName}
              onChange={(event) =>
                setApplicationForm((previous) => ({
                  ...previous,
                  applicantName: event.target.value,
                }))
              }
              className="w-full border border-[#D0D5DD] rounded-md px-3 py-2 outline-none"
              placeholder="Applicant name"
            />

            <input
              value={applicationForm.applicantEmail}
              onChange={(event) =>
                setApplicationForm((previous) => ({
                  ...previous,
                  applicantEmail: event.target.value,
                }))
              }
              className="w-full border border-[#D0D5DD] rounded-md px-3 py-2 outline-none"
              placeholder="Applicant email"
            />

            <input
              value={applicationForm.applicantPhone}
              onChange={(event) =>
                setApplicationForm((previous) => ({
                  ...previous,
                  applicantPhone: event.target.value,
                }))
              }
              className="w-full border border-[#D0D5DD] rounded-md px-3 py-2 outline-none"
              placeholder="Applicant phone (optional)"
            />

            {selectedRequestType?.fields?.map((field) => (
              <div key={field.id}>
                <label className="text-sm text-[#344054]">
                  {field.label}
                  {field.isRequired ? " *" : ""}
                </label>
                <input
                  className="mt-1 w-full border border-[#D0D5DD] rounded-md px-3 py-2 outline-none"
                  value={applicationForm.responses[field.id] || ""}
                  onChange={(event) =>
                    setApplicationForm((previous) => ({
                      ...previous,
                      responses: {
                        ...previous.responses,
                        [field.id]: event.target.value,
                      },
                    }))
                  }
                />
              </div>
            ))}

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                className="px-4 py-2 rounded-md border border-[#D0D5DD] disabled:cursor-not-allowed disabled:opacity-60"
                onClick={() => setApplyModalOpen(false)}
                disabled={submittingApplication}
              >
                Cancel
              </button>
              <button
                type="button"
                className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md bg-[#1234CD] text-white disabled:cursor-wait disabled:bg-slate-300"
                onClick={handleSubmitApplication}
                disabled={submittingApplication}
              >
                {submittingApplication ? (
                  <>
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/50 border-t-white" />
                    Submitting...
                  </>
                ) : (
                  "Submit"
                )}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
